import { useContext } from "react";
import { IPieceList, IPieceListLine } from "../../services/WebApiClient";
import { EditorContext } from "../EditorCommon/EditorContext";
import { SortBy } from "../Table/SortBy";
import { IColumn, Table } from "../Table/Table";
import './PieceListTable.scss'


function formatQuantity(n: number): string {
    const nStr = n.toLocaleString(
        undefined,
        { maximumSignificantDigits: 5 }
    );
    return `x ${nStr}`;
}


export function PieceListTable(props: {
    pieceList: IPieceList
}) {

    const ec = useContext(EditorContext);

    function totalUnitPrice(pll: IPieceListLine): number {
        return pll.unitPriceExVat;
    }

    function formatPrice(priceExVat: number): string {
        const priceInclVat = priceExVat * (1 + props.pieceList.vatRate);
        const formattedPrice = priceInclVat.toLocaleString(
            undefined, //use browser format
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        );
        const translatedPrice = ec.translator("piecelist table row price", "{price} {currency}",
            { price: formattedPrice, currency: props.pieceList.currency }
        )
        return translatedPrice;
    }
    const totalQuantity = props.pieceList.pieceListLines.reduce((previousSum, pll) => pll.quantity + previousSum, 0);

    const vatPercent = (props.pieceList.vatRate * 100).toLocaleString(undefined, {
        maximumFractionDigits: 2
    });



    const columns: IColumn<IPieceListLine>[] = [
        {
            key: "piece-list-external-id",
            className: "piece-list-external-id",
            header: ec.translator("piecelist table header item external id", "Varenr."),
            content: (row) => row.partNo,
            sort: SortBy.String(row => row.partNo)
        },
        {
            key: "piece-list-desc",
            className: "piece-list-desc",
            header: ec.translator("piecelist table header item description", "Vare"),
            footer: ec.translator("piecelist table footer item description", "Total incl. {vat} % moms:", { vat: vatPercent }),
            content: (row) => {
                let texts = [
                    row.name,
                    ...row.descriptions?.map(d => d.name) ?? []
                ];
                return texts.map((t, idx) => <div key={idx}>{t}</div>);
            },
        },
        {
            key: "piece-list-unit-price",
            className: "piece-list-unit-price price-col",
            header: ec.translator("piecelist table header unit price", "Enh. pris"),
            content: (row) => formatPrice(totalUnitPrice(row)),
            sort: SortBy.Number(row => totalUnitPrice(row)),
        },
        {
            key: "piece-list-quantity",
            className: "piece-list-quantity number-col",
            header: ec.translator("piecelist table header quantity", "Antal"),
            footer: formatQuantity(totalQuantity),
            content: (row) => formatQuantity(row.quantity),
            sort: SortBy.Number(row => row.quantity),
        },
        {
            key: "piece-list-price",
            className: "piece-list-price price-col",
            header: ec.translator("piecelist table header total price", "Pris ({currency})", { currency: props.pieceList.currency }),
            footer: formatPrice(props.pieceList.totalPriceExVat),
            content: (row) => formatPrice(totalUnitPrice(row) * row.quantity),
            sort: SortBy.Number(row => totalUnitPrice(row) * row.quantity),
        }
    ];


    return (
        <Table<IPieceListLine>
            columns={columns}
            rows={props.pieceList.pieceListLines}
            tableProps={{ className: "table table-striped table-hover piece-list-table" }}
        />
    );
}
